import { faUsers } from '@fortawesome/pro-regular-svg-icons'
import { Icon, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { formatConversationEventTime } from '@/utils/dateHelpers'
import { ADVISER_ASSISTANT_NAME } from '@/features/adviser-chat/lib/constants'
import type {
  CustomerCaseEvent,
  OperatorIdentity,
} from '@/features/adviser-chat/lib/types'

const operatorName = (operator: OperatorIdentity | null): string =>
  operator?.friendlyName || 'A Heard teammate'

const CASE_RESOLVED_BY = {
  operator: 'operator',
  haven: 'haven',
  automation: 'automation',
  customer: 'customer',
} as const

const eventLabel = (event: CustomerCaseEvent): string => {
  switch (event.kind) {
    case 'handoff':
      return 'Handed off to Heard'
    case 'assigned':
      return `${operatorName(event.operator)} joined`
    case 'unassigned':
      return 'Waiting for a Heard teammate'
    case 'resolved':
      switch (event.resolvedBy) {
        case CASE_RESOLVED_BY.operator:
          return `${operatorName(event.resolver)} resolved this conversation`
        case CASE_RESOLVED_BY.haven:
          return `${ADVISER_ASSISTANT_NAME} resolved this conversation`
        case CASE_RESOLVED_BY.automation:
          return 'This conversation was resolved automatically'
        case CASE_RESOLVED_BY.customer:
          return 'You resolved this conversation'
        default:
          return 'This conversation was resolved'
      }
    case 'reopened':
      return 'This conversation was reopened'
    default:
      return 'Case updated'
  }
}

interface CaseEventRowProps {
  event: CustomerCaseEvent
}

export const CaseEventRow = ({ event }: CaseEventRowProps) => (
  <div className="mx-auto w-full max-w-[640px] py-5 text-center">
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-neutral-200" />
      <div className="flex min-w-0 max-w-[80%] items-center justify-center gap-2">
        {event.kind === 'handoff' && (
          <Icon
            icon={faUsers}
            size="sm"
            color="text-moss-200"
            className="shrink-0"
            aria-hidden
          />
        )}
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.HEADER}
          className="min-w-0 text-center"
        >
          {eventLabel(event)} · {formatConversationEventTime(event.at)}
        </Text>
      </div>
      <div className="h-px flex-1 bg-neutral-200" />
    </div>
    {event.kind === 'handoff' && (
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        className="mx-auto mt-3 max-w-[560px] text-center"
      >
        Summary for the team: {event.summary}
      </Text>
    )}
  </div>
)
